import React, { useState, useEffect } from 'react';
import { Routes, Route, NavLink, useNavigate } from 'react-router-dom';
import {
  FiHome,
  FiAlertTriangle,
  FiActivity,
  FiAlertCircle,
  FiLogOut,
  FiMenu,
  FiX,
  FiUser,
} from 'react-icons/fi';
import clsx from 'clsx';
import { useStaffAuth } from '../contexts/StaffAuthContext';

// Internal dashboard components
import DashboardOverview from '../components/internal/Dashboard';
import AlertManager from '../components/internal/AlertManager';
import MetricsViewer from '../components/internal/MetricsViewer';
import ErrorBrowser from '../components/internal/ErrorBrowser';

const navigation = [
  { name: 'Dashboard', href: '/staff/dashboard', icon: FiHome },
  { name: 'Alerts', href: '/staff/alerts', icon: FiAlertTriangle },
  { name: 'Metrics', href: '/staff/metrics', icon: FiActivity },
  { name: 'Errors', href: '/staff/errors', icon: FiAlertCircle },
];

const StaffDashboard: React.FC = () => {
  const { user, logout } = useStaffAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/staff/login');
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <div
        className={clsx(
          'fixed inset-y-0 left-0 z-50 w-64 bg-gray-900 transform transition-transform duration-300 ease-in-out lg:hidden',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-800">
          <span className="text-white font-semibold">Staff Portal</span>
          <button
            type="button"
            onClick={() => setSidebarOpen(false)}
            className="text-gray-400 hover:text-white"
          >
            <FiX className="w-6 h-6" />
          </button>
        </div>
        <nav className="px-2 py-4 space-y-1">
          {navigation.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-gray-800 text-white'
                    : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                )
              }
            >
              <item.icon className="w-5 h-5" />
              {item.name}
            </NavLink>
          ))}
        </nav>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-50 lg:w-64 lg:flex lg:flex-col">
        <div className="flex flex-col flex-1 bg-gray-900">
          {/* Logo */}
          <div className="flex items-center h-16 px-4 border-b border-gray-800">
            <div className="w-8 h-8 bg-brand-500 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <span className="ml-3 text-white font-semibold">Staff Portal</span>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-gray-800 text-white'
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                  )
                }
              >
                <item.icon className="w-5 h-5" />
                {item.name}
              </NavLink>
            ))}
          </nav>

          {/* User section */}
          <div className="border-t border-gray-800 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center">
                <FiUser className="w-5 h-5 text-gray-300" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-gray-400 truncate">{user?.email}</p>
              </div>
              <button
                type="button"
                onClick={handleLogout}
                className="p-2 text-gray-400 hover:text-white transition-colors"
                title="Sign out"
              >
                <FiLogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-40 h-16 bg-white border-b border-gray-200 flex items-center px-4 lg:px-6">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-gray-500 hover:text-gray-700"
          >
            <FiMenu className="w-6 h-6" />
          </button>

          {/* Public status link */}
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            View public status page &rarr;
          </a>
        </div>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          <Routes>
            <Route path="dashboard" element={<DashboardOverview />} />
            <Route path="alerts" element={<AlertManager />} />
            <Route path="metrics" element={<MetricsViewer />} />
            <Route path="errors" element={<ErrorBrowser />} />
            <Route path="*" element={<DashboardOverview />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default StaffDashboard;
